import Touchable from '@components/Touchable'
import { Box, Stack, useTheme } from '@mui/material'
import { AnimatePresence, useScroll, motion } from 'framer-motion'
import React, {
  FC,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { HiArrowCircleRight, HiArrowCircleLeft } from 'react-icons/hi'

interface ScrollButtonProps extends PropsWithChildren {
  show: boolean
  onClick: () => void
}

const ScrollButton: FC<ScrollButtonProps> = ({ show, children, onClick }) => {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.2 }}
        >
          <Touchable onClick={onClick}>{children}</Touchable>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

interface CarouselProps {
  itemSize: number
  children: ReactNode[]
  spacingPx: number
  hideControls?: boolean
  showPager?: boolean
}

const BUTTON_SIZE = 35

const Carousel: FC<CarouselProps> = ({
  children,
  itemSize,
  spacingPx,
  hideControls,
  showPager,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const { scrollX } = useScroll({ container: scrollRef })
  const lastScrollPosition = useRef(0)
  const isScrolling = useRef(false)

  const [currentPosition, setCurrentPosition] = useState(0)

  const { palette } = useTheme()

  const snapToClosestItem = (scrollPosition: number) => {
    if (!scrollRef.current) return

    const size = itemSize + spacingPx
    const scrollDirection = scrollPosition - lastScrollPosition.current

    let targetIndex: number

    if (Math.abs(scrollDirection) < 2) {
      // Very small movement, snap to closest
      targetIndex = Math.round(scrollPosition / size)
    } else {
      // Directional scrolling
      const currentIndex = scrollPosition / size

      if (scrollDirection > 0) {
        // Scrolling right/forward
        targetIndex = Math.ceil(currentIndex)
      } else {
        // Scrolling left/backward
        targetIndex = Math.floor(currentIndex)
      }
    }

    // Ensure we don't go out of bounds
    targetIndex = Math.max(0, Math.min(targetIndex, children.length - 1))

    const targetScrollPosition = targetIndex * size

    // Only snap if we're not already at the target position
    if (Math.abs(scrollPosition - targetScrollPosition) > 1) {
      scrollRef.current.scrollTo({
        left: targetScrollPosition,
        behavior: 'smooth',
      })
    }
  }

  useEffect(() => {
    const scrollElement = scrollRef.current
    if (!scrollElement) return

    let scrollTimeout: NodeJS.Timeout | undefined

    const handleScroll = () => {
      if (!isScrolling.current) {
        isScrolling.current = true
      }

      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }

      scrollTimeout = setTimeout(() => {
        if (!isScrolling.current) return

        isScrolling.current = false
        const currentScroll = scrollElement.scrollLeft
        snapToClosestItem(currentScroll)
        lastScrollPosition.current = currentScroll
      }, 50)
    }

    scrollElement.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll)
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }
    }
  }, [])

  useEffect(() => {
    scrollX.on('change', (scroll) => {
      const size = itemSize + spacingPx
      const itemIndex = Math.round(scroll / size)
      setCurrentPosition(itemIndex)
    })
  }, [scrollX, itemSize, spacingPx])

  const { leftButtonVisible, rightButtonVisible } = useMemo(() => {
    const scrollPosition = currentPosition * (itemSize + spacingPx)

    if (scrollPosition === 0) return { leftButtonVisible: false, rightButtonVisible: true }

    return {
      leftButtonVisible: scrollPosition > BUTTON_SIZE,
      rightButtonVisible:
        (scrollRef.current?.scrollWidth || 0) -
          scrollPosition -
          (scrollRef.current?.clientWidth || 0) >
        BUTTON_SIZE,
    }
  }, [currentPosition, scrollRef.current, itemSize, spacingPx])

  const { closestItem, scrollRemainder } = useMemo(() => {
    const scrollPosition = currentPosition * (itemSize + spacingPx)
    const size = itemSize + spacingPx
    return {
      closestItem: Math.floor(scrollPosition / size),
      scrollRemainder: scrollPosition % size,
    }
  }, [currentPosition, itemSize, spacingPx])

  const scrollToIndex = (targetIndex: number) => {
    if (!scrollRef.current || targetIndex < 0 || targetIndex >= children.length) return

    const targetItem = scrollRef.current.children[0].children[targetIndex]

    if (!targetItem) return

    const startLocation = scrollRef.current.getBoundingClientRect().left
    const targetItemLocation = targetItem.getBoundingClientRect().left

    const diff = targetItemLocation - startLocation

    scrollRef.current.scrollTo({
      left: diff + scrollRef.current.scrollLeft,
      behavior: 'smooth',
    })
  }

  const scroll = (direction: string) => {
    if (!scrollRef.current) return

    let index =
      direction === 'left'
        ? scrollRemainder === 0
          ? closestItem - 1
          : closestItem
        : closestItem + 1

    scrollToIndex(index)
  }

  const renderPager = () => (
    <Box pt={1} display="flex" justifyContent="center" mb={-2}>
      {children.map((_, i) => (
        <Box
          key={i}
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: i === currentPosition ? 'white' : 'rgba(255, 255, 255, 0.3)',
            mx: 0.25,
            cursor: 'pointer',
          }}
          onClick={() => scrollToIndex(i)}
        />
      ))}
    </Box>
  )

  return (
    <Stack justifyContent="center" position="relative">
      <Stack ref={scrollRef} overflow="scroll">
        <Stack direction="row" spacing={`${spacingPx}px`}>
          {children}
        </Stack>
      </Stack>
      {!hideControls && (
        <Stack position="absolute" ml={-2}>
          <ScrollButton show={leftButtonVisible} onClick={() => scroll('left')}>
            <HiArrowCircleLeft size={BUTTON_SIZE} color={palette.rainbow.blue} />
          </ScrollButton>
        </Stack>
      )}
      {!hideControls && (
        <Stack position="absolute" right={0} mr={-2}>
          <ScrollButton show={rightButtonVisible} onClick={() => scroll('right')}>
            <HiArrowCircleRight size={BUTTON_SIZE} color={palette.rainbow.blue} />
          </ScrollButton>
        </Stack>
      )}
      {showPager && renderPager()}
    </Stack>
  )
}

export default Carousel
